import { HttpClient, HttpErrorResponse, HttpXhrBackend } from '@angular/common/http';
import { ElementRef, inject, Injectable } from '@angular/core';
import { ActivationEnd, NavigationStart, Router } from '@angular/router';
import * as api from '@opentelemetry/api';
import {
  Context,
  Exception,
  Link,
  Span,
  SpanAttributes,
  SpanAttributeValue,
  SpanContext,
  SpanOptions,
  SpanStatus,
  SpanStatusCode,
  TimeInput,
  Tracer,
} from '@opentelemetry/api';
import { v4 as UUIDv4 } from 'uuid';

import { environment } from '../../environments/environment';

interface ComponentTrace {
  id: string;
  name: string;
  startTime: Date;
  component?: any;
  endTime?: Date;
  span?: Span;
  context?: Context;
}

class DummySpan implements Span {
  addEvent(
    _name: string,
    _attributesOrStartTime?: SpanAttributes | TimeInput,
    _startTime?: TimeInput
  ): this {
    return this;
  }

  end(_endTime?: TimeInput): void {}

  isRecording(): boolean {
    return false;
  }

  recordException(_exception: Exception, _time?: TimeInput): void {}

  setAttribute(_key: string, _value: SpanAttributeValue): this {
    return undefined;
  }

  setAttributes(_attributes: SpanAttributes): this {
    return this;
  }

  addLink(_link: Link): this {
    return this;
  }

  addLinks(_links: Link[]): this {
    return this;
  }

  setStatus(_status: SpanStatus): this {
    return this;
  }

  spanContext(): SpanContext {
    return {
      spanId: '',
      traceFlags: 0,
      traceId: '',
    };
  }

  updateName(_name: string): this {
    return this;
  }
}

@Injectable()
export class OpenTelemetryService {
  private static enabled = environment.enableTelemetry;
  private static tracer: Tracer = api.trace.getTracer('semantria-frontend');
  private static context?: Context;

  public static isRoutingEnabled(): boolean {
    return this.enabled && environment.enableRoutingTelemetry;
  }

  public static isClassEnabled(): boolean {
    return this.enabled && environment.enableClassTelemetry;
  }

  public static isComponentEnabled(): boolean {
    return this.enabled && environment.enableComponentTelemetry;
  }

  private getTracer() {
    (window as any).otel = this;
    return OpenTelemetryService.tracer;
  }

  public disable(): void {
    OpenTelemetryService.enabled = false;
  }

  public ping(): Promise<boolean> {
    const httpClient = new HttpClient(new HttpXhrBackend({ build: () => new XMLHttpRequest() }));

    return new Promise((resolve) => {
      const url = environment.openTelemetry.otelcolConfig.url;
      httpClient.get(url).subscribe({
        error: (err: HttpErrorResponse) => {
          if (err.status === 405) {
            resolve(true);
          } else {
            resolve(false);
          }
        },
      });
    });
  }

  public setActiveContext(context: Context) {
    OpenTelemetryService.context = context;
  }

  public getActiveContext() {
    return OpenTelemetryService.context;
  }

  public getActiveSpan() {
    if (!OpenTelemetryService.enabled) {
      return new DummySpan();
    }
    return api.trace.getSpan(api.context.active());
  }

  public startSpan(name: string, options?: SpanOptions, context?: Context): Span {
    if (!OpenTelemetryService.enabled) {
      return new DummySpan();
    }
    return this.getTracer().startSpan(name, options, context);
  }

  public startActiveSpan<T>(
    name: string,
    options?: SpanOptions,
    context?: Context,
    fn?: (span: Span) => T
  ): T {
    if (!OpenTelemetryService.enabled) {
      return fn(new DummySpan());
    }
    return this.getTracer().startActiveSpan(name, options, context, fn);
  }

  public enableRouterTelemetry(router: Router) {
    if (!OpenTelemetryService.isRoutingEnabled()) {
      return;
    }

    const rootContext = api.context.active();
    let rootSpan = null;

    router.events.subscribe((e) => {
      if (!OpenTelemetryService.isRoutingEnabled()) {
        return;
      }

      if (e instanceof NavigationStart) {
        if (!this.getActiveContext()) {
          rootSpan = this.startSpan(`Route ${router.url}`, undefined, rootContext);
        } else {
          OpenTelemetryComponentService.resetComponentMaps();
          rootSpan = this.startSpan(`Route ${router.url}`, undefined, rootContext);
        }
        this.setActiveContext(api.trace.setSpan(rootContext, rootSpan));
      } else if (e instanceof ActivationEnd) {
        const name = router.routerState.snapshot.root.firstChild.routeConfig.path;
        rootSpan.updateName(`Route ${name}`);
        rootSpan.end();
      }
    });
  }
}

@Injectable()
export class OpenTelemetryComponentService {
  // TODO: sort of works, but wonky
  private static componentObjectMap = new WeakMap<any, ComponentTrace>();
  private static componentIdMap = new Map<string, any>();

  private otelService = new OpenTelemetryService();

  public static clearComponentMaps() {
    OpenTelemetryComponentService.componentObjectMap = new WeakMap<any, ComponentTrace>();
    OpenTelemetryComponentService.componentIdMap = new Map<string, any>();
  }

  public static resetComponentMaps() {
    for (const component of OpenTelemetryComponentService.componentIdMap.values()) {
      component._otel.startTime = undefined;
      component._otel.span = undefined;
      component._otel.context = undefined;
    }
  }

  constructor(private elementRef: ElementRef) {}

  private getComponentTraceTree(_component: any): ComponentTrace[] {
    const result = [];
    let element = this.elementRef.nativeElement;

    while (true) {
      const parentElement = element.parentElement.closest('[otel-span]');
      if (!parentElement) {
        break;
      }

      const parentId = parentElement.getAttribute('otel-span');
      const parentComponent = OpenTelemetryComponentService.componentIdMap.get(parentId);
      const parentTrace = OpenTelemetryComponentService.componentObjectMap.get(parentComponent);
      result.push(parentTrace);

      element = parentElement;
    }

    return result.reverse();
  }

  public startComponentTelemetry(component: any) {
    if (!OpenTelemetryService.isComponentEnabled()) {
      return;
    }

    if (!component._otel || component._otel.endTime) {
      component._otel = {
        id: UUIDv4(),
        name: component.constructor.name,
        component,
        startTime: new Date(),
      } as ComponentTrace;
      OpenTelemetryComponentService.componentObjectMap.set(component, component._otel);
      OpenTelemetryComponentService.componentIdMap.set(component._otel.id, component);
    }
  }

  public endComponentTelemetry(component: any) {
    if (!component.otelService) {
      return;
    }

    const data = OpenTelemetryComponentService.componentObjectMap.get(component);
    data.endTime = new Date();

    this.elementRef.nativeElement.setAttribute('otel-span', data.id);
    setTimeout(() => {
      const interval = setInterval(() => {
        const now = new Date();

        let shouldClose = false;
        if (typeof component.isLoading !== 'undefined' && typeof component.isLoading !== 'object') {
          shouldClose = !component.isLoading;
        } else {
          shouldClose = true;
        }

        if (shouldClose) {
          clearInterval(interval);

          const componentTraceTree = this.getComponentTraceTree(component);
          componentTraceTree.push(data);

          let context = this.otelService.getActiveContext();
          for (const componentTrace of componentTraceTree) {
            if (!componentTrace.span) {
              componentTrace.span = this.otelService.startSpan(
                `Component ${componentTrace.name}`,
                { startTime: componentTrace.startTime },
                context
              );
              componentTrace.context = api.trace.setSpan(context, componentTrace.span);
            }
            context = componentTrace.context;
          }

          data.span.end(now);
        }
      }, 50);
    });
  }
}

export function EnableComponentTelemetry(): any {
  return function (target: any, _propertyKey: string, _descriptor: PropertyDescriptor) {
    if (!OpenTelemetryService.isComponentEnabled()) {
      return;
    }

    const ngOnInit = target.ngOnInit;
    target.ngOnInit = function () {
      if (!this.otelService) {
        console.warn(
          `${target.constructor.name} does not appear to be a Component or does not properly declare @EnableComponentTelemetry`
        );
        return;
      }

      this.otelService.startComponentTelemetry(this);

      if (ngOnInit) {
        ngOnInit.call(this);
      }
    };

    const ngAfterContentInit = target.ngAfterContentInit;
    target.ngAfterContentInit = function () {
      let result;
      if (ngAfterContentInit) {
        result = ngAfterContentInit.call(this);
      }

      if (this.otelService) {
        if (result instanceof Promise) {
          result.then(() => this.otelService.endComponentTelemetry(this));
        } else {
          this.otelService.endComponentTelemetry(this);
        }
      }

      return result;
    };

    const ngOnChanges = target.ngOnChanges;
    target.ngOnChanges = function (...args) {
      this.otelService.startComponentTelemetry(this);

      let result;
      if (ngOnChanges) {
        result = ngOnChanges.call(this, ...args);
      }

      if (result instanceof Promise) {
        result.then(() => this.otelService.endComponentTelemetry(this));
      } else {
        this.otelService.endComponentTelemetry(this);
      }
    };
  };
}

export function injectComponentTelemetryService() {
  try {
    const elementRef = inject(ElementRef);
    return new OpenTelemetryComponentService(elementRef);
  } catch (err) {
    return undefined;
  }
}

export function EnableClassTelemetry(): any {
  return function (ctor: any) {
    if (!OpenTelemetryService.isClassEnabled()) {
      return ctor;
    }

    const otelService = new OpenTelemetryService();

    for (const propName of Object.getOwnPropertyNames(ctor.prototype)) {
      const descriptor = Object.getOwnPropertyDescriptor(ctor.prototype, propName);
      if (
        propName === 'constructor' ||
        typeof descriptor.get !== 'undefined' ||
        typeof descriptor.set !== 'undefined'
      ) {
        continue;
      }

      const prop = ctor.prototype[propName];
      const startTime = new Date().getSeconds();
      let nCalls = 0;
      ctor.prototype[propName] = function (...args) {
        const stack = Error().stack;
        if (
          stack.indexOf(`.ngDoCheck`) >= 0 ||
          stack.indexOf('_Template') >= 0 ||
          descriptor.value.disableTelemetry ||
          !OpenTelemetryService.isClassEnabled()
        ) {
          ctor.prototype[propName] = prop;
          const result = prop.call(this, ...args);
          return result;
        }

        nCalls++;
        if (nCalls > 50 && new Date().getSeconds() - startTime < 3) {
          descriptor.value.disableTelemetry = true;
          console.warn(`disabling telemetry for \`${ctor.name}.${propName}: called too many times`);
        }

        let context = api.context.active() as any;
        if (context._currentContext.size === 0) {
          context = otelService.getActiveContext();
        }

        const otelResult = otelService.startActiveSpan(
          `${ctor.name}.${propName}`,
          undefined,
          context,
          (span) => {
            let result: any;
            try {
              result = prop.call(this, ...args);
            } catch (err) {
              span.setStatus({ message: err.toString(), code: SpanStatusCode.ERROR });
              throw err;
            }

            if (result instanceof Promise) {
              result
                .then((_r) => {
                  span.end();
                })
                .catch((err) => {
                  span.setStatus({ message: err.toString(), code: SpanStatusCode.ERROR });
                  span.end();
                });
            } else {
              span.end();
            }
            return result;
          }
        );

        return otelResult;
      };
    }
    return ctor;
  };
}

export function DisableMethodTelemetry() {
  return function (_target: any, _propertyKey: string, descriptor: PropertyDescriptor) {
    descriptor.value.disableTelemetry = true;
  };
}

export function EnableMethodTelemetry() {
  return function (_target: any, _propertyKey: string, _descriptor: PropertyDescriptor) {};
}

export function initTelemetry() {
  return async () => {
    if (environment.enableTelemetry) {
      const otelService = new OpenTelemetryService();
      const enabled = await otelService.ping();
      if (!enabled) {
        console.warn(
          `telemetry has been disabled because endpoint doesn't respond: ${environment.openTelemetry.otelcolConfig.url}`
        );
        otelService.disable();
      } else {
        console.info(
          `telemetry has been enabled with endpoint: ${environment.openTelemetry.otelcolConfig.url}`
        );
      }
    } else {
      console.info('telemetry has been disabled from configuration');
    }
  };
}
